import { RiCloseLine } from 'react-icons/ri';
import { truncatePath } from '../utils/paths';

const truncateLabel = (label) => {
  if (!label) return label;
  const sep = label.indexOf(': ');
  if (sep !== -1) {
    const prefix = label.slice(0, sep + 2);
    const rest = label.slice(sep + 2);
    return prefix + truncatePath(rest, 30);
  }
  return truncatePath(label, 30);
};

const STATUS_CLASS = {
  pending: 'bg-zinc-500/20 text-zinc-300',
  'in-progress': 'bg-accent/20 text-accent',
  completed: 'bg-green-500/20 text-green-300',
  error: 'bg-red-500/20 text-red-200'
};

const BAR_CLASS = {
  pending: 'bg-zinc-500/50',
  'in-progress': 'bg-accent',
  completed: 'bg-green-400/80',
  error: 'bg-red-400/80'
};

const getStatusLabel = (item) => {
  if (item.status === 'completed') return 'Done';
  if (item.status === 'error') return 'Error';
  if (item.status === 'pending') return 'Queued';
  if (item.type === 'upload') return 'Uploading';
  if (item.type === 'download') return 'Downloading';
  return 'Working';
};

const STATUS_PRIORITY = { 'in-progress': 0, pending: 1, error: 2, completed: 3 };

const sortTransfers = (items) =>
  [...items].sort((a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9));

const TransferRow = ({ item, onCancel }) => {
  const isIndeterminate = item.status === 'in-progress' && (item.progress == null || item.progress === 0);
  const isActive = item.status === 'in-progress' || item.status === 'pending';
  const showProgress = item.status === 'in-progress' && item.progress > 0 && item.progress < 100;
  return (
    <div className="rounded-lg border border-border/70 bg-inset/60 px-4 py-2.5 text-base">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="font-medium text-textPrimary truncate" title={item.name}>{truncateLabel(item.name)}</span>
          {item.detail && <span className="text-sm text-red-300 truncate" title={item.detail}>{truncatePath(item.detail, 40)}</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showProgress &&
            <span className="text-sm text-accent font-medium">{item.progress}%</span>
          }
          <span className={`rounded-full px-2.5 py-0.5 text-sm ${STATUS_CLASS[item.status] || STATUS_CLASS['in-progress']}`}>
            {getStatusLabel(item)}
          </span>
          {isActive && onCancel &&
            <button
              type="button"
              className="p-1 rounded hover:bg-white/10 text-textMuted hover:text-red-300 transition"
              onClick={() => onCancel(item.id)}
              title="Cancel">
              <RiCloseLine className="text-base" />
            </button>
          }
        </div>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-border/50">
        {isIndeterminate ?
          <div
            className="h-full w-1/3 bg-accent rounded-full"
            style={{ animation: 'indeterminate 1.2s ease-in-out infinite' }} /> :
          <div
            className={`h-full transition-all duration-300 rounded-full ${BAR_CLASS[item.status] || BAR_CLASS['in-progress']}`}
            style={{ width: item.status === 'completed' ? '100%' : item.status === 'error' ? '100%' : item.status === 'pending' ? '0%' : `${item.progress || 0}%` }} />
        }
      </div>
      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
};

const TransferList = ({ transfers, onCancel, onCancelAll }) => {
  const sorted = sortTransfers(transfers);

  const hasActive = transfers.some((t) =>
    (t.status === 'in-progress' || t.status === 'pending')
  );

  return (
    <div className="grid gap-1.5 overflow-y-auto min-h-0">
      {transfers.length === 0 ?
        <p className="text-base text-textMuted">No activity yet.</p> :
        <>
          {hasActive &&
            <div className="flex items-center justify-end px-1 py-1">
              {onCancelAll &&
                <button
                  type="button"
                  className="ghost-button px-2.5 py-1 text-sm text-red-300 hover:text-red-200"
                  onClick={onCancelAll}>
                  Cancel all
                </button>
              }
            </div>
          }
          {sorted.map((item) => <TransferRow key={item.id} item={item} onCancel={onCancel} />)}
        </>
      }
    </div>
  );
};


export default TransferList;
